import EdTaskBoardPencil from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/pencil/ed-wbo-drawer-pencil';
import EdTaskBoardCursor from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/cursor/ed-wbo-drawer-cursor';
import EdTaskBoardLine from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/line/ed-wbo-drawer-line';
import EdTaskBoardHand from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/hand/ed-wbo-drawer-hand';
import EdTaskBoardText from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/text/ed-wbo-drawer-text';
import EdTaskBoardEraser from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/eraser/ed-wbo-drawer-eraser';
import EdTaskBoardEraserAll
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/eraser-all/ed-wbo-drawer-eraser-all';
import EdTaskBoardRecreateShapes
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/undo-redo/ed-wbo-drawer-recreate-shapes';
import EdTaskBoardManageState
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/undo-redo/ed-wbo-drawer-manage-state';
import EdTaskBoardUndo
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/undo-redo/undo/ed-wbo-drawer-undo';
import EdTaskBoardRedo
    from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/undo-redo/redo/ed-wbo-drawer-redo';
import WboDrawerConfig from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-config.interface';
import WboDrawerTools from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/wbo-drawer-tools';
import BoardTool from '@/components/common/ed-board/ed-board-tools/board-tool.interface';
import { WboMsg } from '../wbo-client/model/wbo-annotation.model';

export default class EdWboDrawer {
    private readonly wboDrawerTools: WboDrawerTools;

    private taskBoardPencil: EdTaskBoardPencil;
    private taskBoardCursor: EdTaskBoardCursor;
    private taskBoardLine: EdTaskBoardLine;
    private taskBoardHand: EdTaskBoardHand;
    private taskBoardText: EdTaskBoardText;
    private taskBoardEraser: EdTaskBoardEraser;
    private taskBoardEraserAll: EdTaskBoardEraserAll;
    private readonly recreateShapes: EdTaskBoardRecreateShapes;
    private readonly manageState: EdTaskBoardManageState;
    private taskBoardUndo: EdTaskBoardUndo;
    private taskBoardRedo: EdTaskBoardRedo;
    constructor(private config: WboDrawerConfig, setCursorBoardMutation: any = null) {
        this.wboDrawerTools = new WboDrawerTools(config);
        this.configureToolsHooks();
        this.taskBoardPencil    = new EdTaskBoardPencil(this.wboDrawerTools, setCursorBoardMutation);
        this.taskBoardCursor    = new EdTaskBoardCursor(this.wboDrawerTools);
        this.taskBoardLine      = new EdTaskBoardLine(this.wboDrawerTools, setCursorBoardMutation);
        this.taskBoardHand      = new EdTaskBoardHand(this.wboDrawerTools, setCursorBoardMutation);
        this.taskBoardText      = new EdTaskBoardText(this.wboDrawerTools);
        this.taskBoardEraser    = new EdTaskBoardEraser(this.wboDrawerTools, setCursorBoardMutation);
        this.taskBoardEraserAll = new EdTaskBoardEraserAll(this.wboDrawerTools);
        this.recreateShapes     = new EdTaskBoardRecreateShapes(this.wboDrawerTools);
        this.manageState        = new EdTaskBoardManageState(this.wboDrawerTools);
        this.taskBoardUndo      = new EdTaskBoardUndo(this.wboDrawerTools, this.manageState, this.recreateShapes);
        this.taskBoardRedo      = new EdTaskBoardRedo(this.wboDrawerTools, this.manageState, this.recreateShapes);
        if (config.extraTools) {
            config.extraTools.forEach(extraToolConstructor => extraToolConstructor(this.wboDrawerTools));
        }
    }


    getOpacity(): number {
        return this.wboDrawerTools.getOpacity();
    }

    setOpacity(value: number) {
        this.wboDrawerTools.opacity = value;
    }

    getSize(): number {
        return this.wboDrawerTools.size;
    }

    setSize(value: number) {
        this.wboDrawerTools.setSize(value);
    }


    /**
     * Gets the currently active tool
     */
    get currentTool(): BoardTool {
        return this.wboDrawerTools.curTool;
    }

    get settings(): WboDrawerConfig {
        return this.config;
    }


    /**
     * Set the given tool name as the current tool being used.
     * @param toolName The tool name to be set as the new active tool.
     * @param isSecondary Some tools have secondary tools. For instance, the Pencil tool has a secondary tool that is
     * the White-Out. So, if a tool that is a secondary tools needs to be activated, the primary tool name must be
     * passed and the isSecondary must be passed with value true.
     */
    public setAsCurrentTool(toolName: string, isSecondary?: boolean) {
        this.wboDrawerTools.changeTool(toolName, isSecondary);
    }

    public unsetCurrentTool() {
        this.wboDrawerTools.unsetCurrentTool();
    }

    public setDefaultTool() {
        this.wboDrawerTools.changeTool('Hand', false);
    }

    /**
     * Returns the instance of tool given its name.
     */
    public getToolByName(toolName: string) {
        return this.wboDrawerTools.list[toolName];
    }

    public handleMessage(message): Promise<any> {
        if (!message) {
            return Promise.resolve();
        }
        // If a message has no tool, but it is of type 'child', assume its tool is 'Pencil'
        if (message.type === 'child' && !message.tool) {
            message.tool = 'Pencil';
        }
        if (!message.tool && !message._children) {
            return Promise.resolve();
        }
        if (message.tool) {
            this.messageForTool(message);
        }
        if (this.wboDrawerTools.persistenceFunction
            && message.tool !== 'Cursor'
            && this.wboDrawerTools.username == message.userName
            && (message.boardName || message.id)) {
            setTimeout(() => this.wboDrawerTools.persistenceFunction(message));
        }


        if (message._children) {
            return this.batchCall(message._children);
        } else {
            return Promise.resolve();
        }
    }

    public messageForTool(message) {
        this.wboDrawerTools.addAnnotationId(message.id, message.userName);
        let name = message.tool;
        let tool = this.wboDrawerTools.list[name];

        if (message.type === 'delete') {
            //If the message is a delete, we remove the pencil path data cache  
            this.taskBoardPencil.removePencilPathDataCacheById(message.id);
        }
        
        if ((message.type === 'update' || message.type === 'delete') && message.userName === this.wboDrawerTools.username)  {
            // Someone else changed annotations made by this user
                this.cleanUndoRedoIfNecessary([message.id]);
            }

        if (tool) {
            this.wboDrawerTools.applyHooks(this.wboDrawerTools.messageHooks, message);
            tool.draw(message, false);
        } else {
            ///We received a message destinated to a tool that we don't have
            //So we add it to the pending messages
            if (!this.wboDrawerTools.pendingMessages[name]) {
                this.wboDrawerTools.pendingMessages[name] = [message];
            }
            else {
                this.wboDrawerTools.pendingMessages[name].push(message);
            }
        }

        if (message.tool !== 'Hand' && message.deltax != null && message.deltay != null) {
            //this message has special info for the mover
            this.messageForTool({ tool: 'Hand', type: 'update', deltax: message.deltax || 0, deltay: message.deltay || 0, id: message.id });
        }
    }

    private batchCall(args: any): Promise<any> {
        const BATCH_SIZE = 1024;
        if (args.length === 0) {
            return Promise.resolve();
        } else {
            const batch = args.slice(0, BATCH_SIZE);
            const rest = args.slice(BATCH_SIZE);
            // Only flush path data to the DOM for the last element of the batch
            for (let i = 0; i < batch.length; i++) {
                batch[i].flushPathData = i == batch.length - 1;
            }
            return Promise.all(batch.map(msg => this.handleMessage(msg)))
                .then(() => new Promise(requestAnimationFrame))
                .then(() => this.batchCall(rest));
        }
    }

    private configureToolsHooks(): void {
        this.wboDrawerTools.toolHooks = [
            //checkToolAttributes
            (tool: BoardTool) => {
                if (typeof (tool.name) !== 'string') throw 'A tool must have a name';
                if (typeof (tool.listeners) !== 'object') {
                    tool.listeners = {};
                }
                if (typeof (tool.onstart) !== 'function') {
                    tool.onstart = function () {
                    };
                }
                if (typeof (tool.onquit) !== 'function') {
                    tool.onquit = function () {
                    };
                }
            },
            //compileListeners
            (tool: BoardTool) => {
                //compile listeners into compiledListeners
                let listeners = tool.listeners;

                //A tool may provide precompiled listeners
                let compiled = tool.compiledListeners || {};
                tool.compiledListeners = compiled;

                let compile = (listener: Function) => { //closure
                    return ((evt: MouseEvent) => {
                        let x = evt.offsetX;
                        let y = evt.offsetY;

                        const point = this.calculatePreciseCoordinates(evt);
                        return listener(point.x, point.y, evt, false);
                    });
                }

                let compileTouch = (listener: Function) => { //closure
                    return ((evt: TouchEvent) => {
                        //Currently, we don't handle multitouch
                        if (evt.changedTouches.length === 1) {
                            /*
                              TODO: TOUCH event points
                            */
                            let touch = evt.changedTouches[0];
                            const point = this.calculatePreciseCoordinates(touch);
                            return listener(point.x, point.y, evt, true);
                        }
                        return true;
                    });
                }

                let wrapUnsetHover = (f: Function, toolName) => {
                    return (evt) => {
                        let activeElement: any = document.activeElement;
                        activeElement && activeElement.blur && activeElement.blur();
                        return f(evt);
                    };
                }

                if (listeners.press) {
                    compiled['mousedown'] = wrapUnsetHover(compile(listeners.press), tool.name);
                    compiled['touchstart'] = wrapUnsetHover(compileTouch(listeners.press), tool.name);
                }
                if (listeners.move) {
                    compiled['mousemove'] = compile(listeners.move);
                    compiled['touchmove'] = compileTouch(listeners.move);
                }
                if (listeners.release) {
                    let release = compile(listeners.release),
                        releaseTouch = compileTouch(listeners.release);
                    compiled['mouseup'] = release;

                    if (!this.wboDrawerTools.isIE) compiled['mouseleave'] = release;
                    compiled['touchleave'] = releaseTouch;
                    compiled['touchend'] = releaseTouch;
                    compiled['touchcancel'] = releaseTouch;
                }
                if (listeners.leave) {
                    compiled['mouseleave'] = compile(listeners.leave);
                }
            }
        ];
    }


    private applyHooks(hooks, object) {
    }


    setColor(colorValue: string) {
        this.wboDrawerTools.color = colorValue;
    }

    getColor(): string {
        return this.wboDrawerTools.color;
    }

    beforeDraw(beforeDrawCallback: Function) {
        if (beforeDrawCallback) {
            this.wboDrawerTools.beforeDrawCallback = beforeDrawCallback;
        }
    }

    onDraw(drawCallback: Function) {
        if (drawCallback) {
            this.wboDrawerTools.drawCallback = drawCallback;
        }
    }

    onSend(sendCallback: Function) {
        if (sendCallback) {
            this.wboDrawerTools.sendCallback = sendCallback;
        }
    }

    onStopStroke(stopStrokeCallback: Function) {
        if (stopStrokeCallback) {
            this.wboDrawerTools.stopStrokeCallback = stopStrokeCallback;
        }
    }

    canSendMessage(canSendMessageFunction: Function) {
        if (canSendMessageFunction) {
            this.wboDrawerTools.canSendMessage = canSendMessageFunction;
        }
    }

    updateBoardWorkInProgress(updateBoardWorkInProgressFunction: (b: boolean) => void) {
        if (updateBoardWorkInProgressFunction) {
            this.wboDrawerTools.updateBoardWorkInProgress = updateBoardWorkInProgressFunction;
        }
    }

    broadcastMessage(broadcastMessageFunction: Function) {
        if (broadcastMessageFunction) {
            this.wboDrawerTools.broadcastMessage = broadcastMessageFunction;
        }
    }

    getScale(): number {
        return this.wboDrawerTools.scale;
    }

    setScale(scale: number, yScale?: number) {
        // this.wboDrawerTools.scale = scale;
        this.wboDrawerTools.setScale(scale, yScale);
    }

    changeTool(toolName: string, isSecondary: boolean = false): void {
        this.wboDrawerTools.changeTool(toolName, isSecondary)
    }

    removeClientElements(elementsToDelete: string[], username: string) {
        this.manageState.deleteAnnotations(elementsToDelete, true);
        this.taskBoardEraserAll.removeClientElements(elementsToDelete);
        elementsToDelete.forEach(element => this.removePencilPathDataCacheById(element));
    }

    removeAnnotationsFromMessage(msg: WboMsg): void {
        if (msg && msg.elementsToDelete && msg.elementsToDelete.length > 0) {
            this.removeClientElements(msg.elementsToDelete, msg.userName);
            this.manageState.cleanUndoRedoStacks();
        }
    }

    removeAllCurrentUserAnnotations(): void {
        const annotationIds = this.wboDrawerTools.annotations;
        const annotationOwners = this.wboDrawerTools.annotationOwners;
        const currentUsername = this.wboDrawerTools.username;
        const isHost = this.wboDrawerTools.isHostUser;
        const elementsToDelete = annotationIds
            .filter(annotation => isHost ? true : annotationOwners[annotation]?.userName === currentUsername);
        this.manageState.deleteAnnotations(elementsToDelete, true);
        this.taskBoardEraserAll.removeClientElements(elementsToDelete);
        elementsToDelete.forEach(element => this.removePencilPathDataCacheById(element));
    }

    clearPathDataCache() {
        if (this.taskBoardPencil.clearPencilPathDataCache) {
            this.taskBoardPencil.clearPencilPathDataCache();
        }
    }

    public removePencilPathDataCacheById(annotationId: string): void {
        this.taskBoardPencil.removePencilPathDataCacheById(annotationId);
    }

    public manageAnnotation(data: any, isMadeByUser: boolean): void {
        this.manageState.manageAnnotation(data, isMadeByUser);
    }

    public clearDrawings(): void {
        this.clearPathDataCache();
        this.wboDrawerTools.clearDrawingArea();
    }

    public bringToFront(annotationId: string): void {
        this.wboDrawerTools.bringToFront(annotationId);
    }

    /**
     * Should be called when the drawer is no longer needed.
     * If it is not called, some event listeners will remain which could cause memory leaks.
     */
    public dispose(): void {
        Object.keys(this.wboDrawerTools.list)
          .map(toolName => this.wboDrawerTools.list[toolName])
          .forEach(tool => this.wboDrawerTools.removeToolListeners(tool));
    }

    private calculatePreciseCoordinates(evt: MouseEvent | Touch) {
        const boardBoundingClient = this.wboDrawerTools.board.getBoundingClientRect();
        const x: number = (evt.pageX - boardBoundingClient.left) / this.wboDrawerTools.getScale();
        const y: number = (evt.pageY - boardBoundingClient.top) / this.wboDrawerTools.getYScale();
        const round = (value: number): number => Math.round(value * 10000) / 10000; // Round to 3 decimal places
        return { x: round(x), y: round(y) };
    }

    private cleanUndoRedoIfNecessary(annotationList): void {
        if (annotationList?.length === 0) {
            return;
        }
        this.manageState.clearUndoRedoStacksIfAnnotationIdsExist(annotationList);
    }
}
